import React, { useContext, useEffect} from "react";
import { Box, Button, styled, useTheme } from "@mui/material";
import { Routes as MainMenu } from "entities/routes";
import { useNavigate, useLocation } from "react-router-dom";
import { RoutesInterface } from "entities/interfaces";
import { TextSizeContext } from "data/index";
import { AppWidthMin } from "entities/constants";
import { useTranslation } from "react-i18next";
import { useDebounceNavigation } from "../../../hooks/useDebounceNavigation";
import { useViewportHeight } from "../../../hooks/useViewPortHeight"; 


const Drawer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.95)' 
    : 'rgba(18, 18, 18, 0.95)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  width: "100%",
  height: '4.5rem',
  minHeight: '4.5rem', // Zusätzlich für Stabilität
  maxHeight: '4.5rem', // Verhindert Größenänderungen
  border: `1px solid ${theme.palette.mode === 'light' 
    ? 'rgba(0, 0, 0, 0.08)' 
    : 'rgba(255, 255, 255, 0.08)'}`,
  borderTop: `1px solid ${theme.palette.mode === 'light' 
    ? 'rgba(0, 0, 0, 0.12)' 
    : 'rgba(255, 255, 255, 0.12)'}`,
  borderBottom: 'none',
  borderLeft: 'none',
  borderRight: 'none',
  display: "flex",
  justifyContent: "space-evenly",
  alignItems: 'center',
  zIndex: 9999, // Höhere z-index
  position: 'fixed', // Geändert von absolute zu fixed
  bottom: '0',
  left: '0',
  right: '0',
  boxShadow: theme.palette.mode === 'light'
    ? '0px -2px 16px rgba(0, 0, 0, 0.08)'
    : '0px -2px 16px rgba(0, 0, 0, 0.32)',
  minWidth: AppWidthMin,
  padding: '0 1rem',
  paddingBottom: 'env(safe-area-inset-bottom)', // Für iPhones mit Home Indicator
  transform: 'translateZ(0)',
  WebkitTransform: 'translateZ(0)',
  willChange: 'transform',
  backfaceVisibility: 'hidden',
  perspective: '1000px',
  // Zusätzliche mobile Fixes
  WebkitOverflowScrolling: 'touch',
  // CSS supports für iOS
  '@supports (-webkit-appearance: none)': {
    bottom: 'max(0px, env(safe-area-inset-bottom))',
  },
}));

const StyledButton = styled(Button)<{ ishome?: string; isactive?: string }>(
  ({ theme, ishome, isactive }) => ({
    backgroundColor: 'transparent',
    borderRadius: ishome ? "1.5rem" : "1.2rem",
    width: ishome ? '3.5rem' : "3rem",
    height: ishome ? '3.5rem' : "3rem",
    minWidth: 'unset',
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    padding: '0.5rem',
    position: 'relative',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    border: 'none',
    
    ...(isactive === 'true' && {
      backgroundColor: `${theme.palette.primary.main}20`,
      transform: 'translateY(-2px)',
      boxShadow: `0px 4px 12px ${theme.palette.primary.main}40`,
    }),
    
    ':hover': {
      backgroundColor: isactive === 'true' 
        ? `${theme.palette.primary.main}30` 
        : theme.palette.mode === 'light' 
          ? 'rgba(0, 0, 0, 0.04)' 
          : 'rgba(255, 255, 255, 0.08)',
      transform: isactive === 'true' ? 'translateY(-2px)' : 'translateY(-1px)',
    },
    
    ':active': {
      transform: 'translateY(0px)',
    },
    
    '& svg': {
      fontSize: ishome ? '1.5rem' : '1.3rem',
      transition: 'all 0.2s ease',
    },
  })
);

interface NavButtonProps {
  menu: RoutesInterface;
  isHome?: boolean;
}

const NavButton: React.FC<NavButtonProps> = ({
  menu,
  isHome = false,
}) => {
  const { pathname } = useLocation();
  const active = pathname.split("/")[1];
  const theme = useTheme();
  const { t } = useTranslation();
  const { debouncedNavigate, cleanup } = useDebounceNavigation(300);
  
  const isActive = menu.title.toLocaleLowerCase().replaceAll(" ", "") === active;

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleClick = (): void => {
    debouncedNavigate(menu.link);
  };

  return (
    <StyledButton
      onClick={handleClick}
      ishome={isHome ? "true" : undefined}
      isactive={isActive ? "true" : undefined}
      sx={{
        color: isActive 
          ? theme.palette.primary.main
          : theme.palette.mode === 'light' 
            ? theme.palette.grey[600] 
            : theme.palette.grey[400]
      }}
      aria-label={menu.title}
    >
      {isActive ? menu.iconActiveMobile : menu.iconMobile}
    </StyledButton>
  );
};

const MobileNavbar: React.FC = () => {
  const { height } = useViewportHeight(); // Hook verwenden
  

  return (
    <Drawer
      sx={{
        // Force die Navbar immer sichtbar zu bleiben
        bottom: '0 !important',
        // Optional: Dynamische Positionierung basierend auf Viewport
        // bottom: `${Math.max(0, window.innerHeight - height)}px !important`,
      }}
    >
      <NavButton menu={MainMenu[0]} isHome />
      <NavButton menu={MainMenu[1]} />
      <NavButton menu={MainMenu[2]} />
      <NavButton menu={MainMenu[3]} />
      <NavButton menu={MainMenu[4]} />
    </Drawer>
  );
};

export default MobileNavbar;